import React, { useCallback, useMemo } from 'react';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { Button, Checkbox, CircularProgress, Stack } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import to from 'await-to-js';
import { useCommonTranslation } from 'c-translation';
import { NetworkRequestState } from 'c-data-layer';
import { Alert } from 'c-components';
import useNewCampaignSchedule from 'c-main/Components/Schedule/useNewCampaignSchedule';
import {
    csvStringToBlob,
    downloadContentType,
    selectAllState,
    toggleAllUnknowns,
    toggleUnknownSelection,
} from './unknownDisplaysLib';

// Extract filename from `content-disposition`
function getFilenameFromContentDisposition(disposition, fallback = 'data.csv') {
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(disposition);
    if (!match) return fallback;
    try {
        // Handle RFC5987 encoding like UTF-8''name.csv
        return decodeURIComponent(match[1]);
    } catch {
        return match[1];
    }
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'data.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

const UnknownTab = ({ id }) => {
    const {
        start: removeStart,
        isLoading: isRemoving,
        error: removeError,
        reset: removeReset,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.removeUnknownDisplays);
    const {
        start: importStart,
        isLoading: isImporting,
        reset,
        requestState,
        error,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.reImportUnknownDisplays);
    const {
        start: downloadStart,
        reset: downloadReset,
        error: downloadError,
        isLoading: isDownloading,
    } = useAPIClientRequest(ApiClient.Entities.Campaign.downloadUnknownDisplays);
    const t = useCommonTranslation();
    const {
        listUnknownDisplays,
        unknownLoading,
        setSelectedUnknowns,
        selectedUnknowns,
        unknownDisplays,
    } = useNewCampaignSchedule(id);

    const importUnknownDisplays = useCallback(async () => {
        const [err] = await to(importStart(id));
        if (!err) {
            listUnknownDisplays();
        }
    }, [id, importStart, listUnknownDisplays]);

    const removeUnknownDisplays = useCallback(async () => {
        const [err] = await to(removeStart(id, { ids: selectedUnknowns }));
        if (!err) {
            setSelectedUnknowns([]);
            listUnknownDisplays();
        }
    }, [removeStart, id, selectedUnknowns, setSelectedUnknowns, listUnknownDisplays]);

    const downloadUnknownDisplays = useCallback(async () => {
        const [err, succ] = await to(downloadStart(id));
        if (!err && succ) {
            const csvString = succ?.data;

            const filename = `unknown_displays_${id}`;

            const contentType = downloadContentType(succ?.headers?.['content-type']);

            const blob = csvStringToBlob(csvString, contentType, false);

            downloadBlob(blob, filename);
        }
    }, [downloadStart, id]);

    const toggleSelection = useCallback(
        rowId => {
            setSelectedUnknowns(prev => toggleUnknownSelection(prev, rowId));
        },
        [setSelectedUnknowns],
    );

    const toggleSelectAll = useCallback(() => {
        setSelectedUnknowns(prev => toggleAllUnknowns(prev, unknownDisplays));
    }, [setSelectedUnknowns, unknownDisplays]);

    const columns = useMemo(
        () => [
            {
                field: 'select',
                headerName: '',
                width: 100,
                sortable: false,
                renderHeader: () => (
                    <Checkbox
                        {...selectAllState(selectedUnknowns.length, unknownDisplays.length)}
                        onChange={toggleSelectAll}
                    />
                ),
                renderCell: ({ row }) => (
                    <Checkbox
                        checked={selectedUnknowns.includes(row.id)}
                        onChange={() => toggleSelection(row.id)}
                    />
                ),
            },
            {
                field: 'id',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.unknownTab.table.id'),
                width: 90,
            },
            {
                field: 'frame_id',
                headerName: t('Modules.Main.Campaigns.CampaignSchedule.unknownTab.table.frameId'),
                width: 150,
            },
        ],
        [t, selectedUnknowns, unknownDisplays?.length, toggleSelectAll, toggleSelection],
    );

    return (
        <Stack spacing={2} p={2}>
            <Stack direction="row" spacing={2}>
                <Stack>
                    <Button
                        onClick={importUnknownDisplays}
                        variant="contained"
                        disabled={isImporting}
                    >
                        {isImporting ? (
                            <CircularProgress size={20} />
                        ) : (
                            t(
                                'Modules.Main.Campaigns.CampaignSchedule.unknownsTab.syncUnknownDisplays',
                            )
                        )}
                    </Button>
                    {requestState === NetworkRequestState.Success && (
                        <Alert severity="success" onClose={reset}>
                            {t('Modules.Main.Campaigns.CampaignSchedule.unknownTab.queued')}
                        </Alert>
                    )}
                    {error && (
                        <Alert severity="error" onClose={reset}>
                            {String(error)}
                        </Alert>
                    )}
                </Stack>
                <Stack>
                    <Button
                        onClick={removeUnknownDisplays}
                        variant="contained"
                        color="error"
                        disabled={selectedUnknowns.length === 0 || isRemoving}
                    >
                        {isRemoving ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('Modules.Main.Campaigns.CampaignSchedule.unknownsTab.removeSelected')
                        )}
                    </Button>
                    {removeError && (
                        <Alert severity="error" onClose={removeReset}>
                            {String(removeError)}
                        </Alert>
                    )}
                </Stack>
                <Stack>
                    <Button
                        onClick={downloadUnknownDisplays}
                        disabled={isDownloading}
                        variant="contained"
                    >
                        {t(
                            'Modules.Main.Campaigns.CampaignSchedule.unknownsTab.downloadUnknownDisplays',
                        )}
                    </Button>
                    {downloadError && (
                        <Alert severity="error" onClose={downloadReset}>
                            {String(downloadError)}
                        </Alert>
                    )}
                </Stack>
            </Stack>
            <DataGridPro
                columns={columns}
                rows={unknownDisplays}
                loading={unknownLoading}
                autoHeight
            />
        </Stack>
    );
};

export default UnknownTab;
